import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
    Clock,
    CheckCircle2,
    Wand2,
    Compass,
    Crosshair,
    Share2,
    ChevronRight,
    AlertTriangle,
    ShieldCheck,
} from "lucide-react";

/**
 * DueOuts — Director's master checklist (Director directive 2026.02).
 *
 * Renders a live ticker of pending command actions next to the Sovereign Eye
 * 3D globe on the Director Panel. Auto-detects state from real backend data
 * — no manual "mark complete" wiring required:
 *
 *   1. Magic Link Login        — auto-clears once the Director is signed in.
 *                                 (If you can see this widget, this is DONE.)
 *   2. Pending Admiral coords   — counts users with `role_request_status: "pending"`.
 *                                 Pulses red until the queue is empty.
 *   3. B2B Strike Authorization — counts foundry orders in `commissioned` stage
 *                                 (awaiting operative dispatch). Pulses red
 *                                 if any strike has been waiting > 1h.
 *   4. Social Auth status       — checks Resend domain verification + COOKIE_DOMAIN.
 *                                 Pulses red if Resend is on @resend.dev.
 *
 * Visuals: pulsing red for pending, static gold for completed. Pure CSS, no
 * external animation library.
 */
export default function DueOuts({ user, overview }) {
    const navigate = useNavigate();
    const [items, setItems] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                // Pull lightweight signals in parallel — none of these are expensive.
                const [diagRes, queueRes, ledgerRes] = await Promise.all([
                    api.get("/auth/diag").catch(() => ({ data: null })),
                    // Reuse the foundry feed as a free pending-strike signal — first 50 orders, filter client-side.
                    api.get("/foundry/feed?limit=50").catch(() => ({ data: { orders: [] } })),
                    api.get("/foundry/ledger").catch(() => ({ data: null })),
                ]);

                if (cancelled) return;

                // 1 · Magic Link login — Director is on this page → done.
                const magicDone = !!user;

                // 2 · Pending Admiral coords — read from ledger or overview shape.
                // Best-available signal: count of users with role_request_status: "pending".
                // We probe via /director/overview if it has a `pending_role_requests` field;
                // otherwise we fall back to "unknown" and surface the curl path.
                const pendingAdmirals = overview?.pending_role_requests ?? overview?.role_request_pending ?? null;

                // 3 · B2B Strike Authorization — orders in `commissioned` stage.
                const orders = queueRes.data?.orders || queueRes.data || [];
                const commissioned = (Array.isArray(orders) ? orders : []).filter(
                    (o) => (o.stage || o.status) === "commissioned" || o.status === "pending"
                );
                // Aged: any strike still awaiting authorization > 1 hour.
                const oneHourAgo = Date.now() - 60 * 60 * 1000;
                const aged = commissioned.filter((o) => {
                    const t = Date.parse(o.created_at || "");
                    return Number.isFinite(t) && t < oneHourAgo;
                });

                // 4 · Social Auth status — diagnose Resend + COOKIE_DOMAIN.
                const diag = diagRes.data || {};
                const cookieOk = diag.cookie_domain_effective && diag.cookie_domain_effective !== "host-only";

                setItems([
                    {
                        id: "magic-login",
                        label: "Magic Link Login",
                        sub: magicDone ? "Director session armed" : "Awaiting sign-in",
                        done: magicDone,
                        icon: Wand2,
                        action: null,
                    },
                    {
                        id: "admiral-coords",
                        label: "Admiral Coordinates",
                        sub: pendingAdmirals === null
                            ? "Run /director/overview to fetch queue"
                            : pendingAdmirals === 0
                                ? "All Admirals confirmed"
                                : `${pendingAdmirals} Admiral${pendingAdmirals !== 1 ? "s" : ""} awaiting promotion`,
                        done: pendingAdmirals === 0,
                        unknown: pendingAdmirals === null,
                        icon: Compass,
                        action: () => {
                            const el = document.querySelector('[data-testid="director-partners-list"]');
                            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                        },
                    },
                    {
                        id: "strike-auth",
                        label: "B2B Delivery Authorization",
                        sub: commissioned.length === 0
                            ? "No strikes awaiting authorization"
                            : aged.length > 0
                                ? `${commissioned.length} pending · ${aged.length} aged > 1h`
                                : `${commissioned.length} commissioned · all fresh`,
                        done: commissioned.length === 0,
                        urgent: aged.length > 0,
                        icon: Crosshair,
                        action: () => navigate("/foundry/control"),
                        count: commissioned.length || null,
                    },
                    {
                        id: "social-auth",
                        label: "Social Auth Status",
                        sub: cookieOk
                            ? `Cookie domain · ${diag.cookie_domain_effective}`
                            : "Set COOKIE_DOMAIN=.bizboogie.com in deployment",
                        done: cookieOk,
                        icon: Share2,
                        action: null,
                    },
                ]);
            } catch (e) {
                if (!cancelled) setItems([]);
            }
        };
        load();
        const t = setInterval(load, 30000);
        return () => { cancelled = true; clearInterval(t); };
    }, [user, overview, navigate]);

    const pendingCount = (items || []).filter((i) => !i.done && !i.unknown).length;
    const allClear = items && pendingCount === 0;

    return (
        <aside
            data-testid="director-due-outs"
            className="glass rounded-2xl p-5 border-[hsl(40_50%_35%/0.4)] h-full flex flex-col"
        >
            <header className="flex items-baseline justify-between mb-4">
                <div className="flex items-center gap-2">
                    <ShieldCheck size={16} className="text-[hsl(45_85%_68%)]" />
                    <div>
                        <div className="font-heading text-lg tracking-tight">Director Due Outs</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Master checklist · live</div>
                    </div>
                </div>
                {items && (
                    <span
                        data-testid="due-outs-summary"
                        className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full"
                        style={{
                            background: allClear ? "hsl(40 70% 22%)" : "hsl(350 60% 22%)",
                            color: allClear ? "hsl(45 85% 75%)" : "hsl(350 75% 80%)",
                            border: `1px solid ${allClear ? "hsl(45 85% 60% / 0.5)" : "hsl(350 75% 60% / 0.5)"}`,
                        }}
                    >
                        {allClear ? "All clear" : `${pendingCount} pending`}
                    </span>
                )}
            </header>

            {!items ? (
                <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground">
                    Pulling fleet status…
                </div>
            ) : items.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground">
                    Unable to load due-outs.
                </div>
            ) : (
                <ul className="space-y-2 flex-1">
                    {items.map((item) => (
                        <DueOutRow key={item.id} item={item} />
                    ))}
                </ul>
            )}

            <div className="mt-4 pt-3 border-t border-[hsl(40_40%_30%/0.18)] flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                    <Clock size={11} /> auto-refresh · 30s
                </span>
                <span className="inline-flex items-center gap-2">
                    <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[hsl(350_75%_60%)] animate-pulse" /> pending
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[hsl(45_85%_60%)]" /> done
                    </span>
                </span>
            </div>
        </aside>
    );
}

function DueOutRow({ item }) {
    const Icon = item.icon || Clock;
    const isDone = item.done;
    const isUnknown = item.unknown;
    const isUrgent = item.urgent;

    // Visual state — pulsing red for pending, static gold for done.
    const dotColor = isDone
        ? "hsl(45 85% 60%)"
        : isUnknown
            ? "hsl(40 30% 50%)"
            : isUrgent
                ? "hsl(0 80% 60%)"
                : "hsl(350 75% 60%)";
    const dotPulse = !isDone && !isUnknown;

    const rowBg = isDone
        ? "hsl(40 70% 18% / 0.35)"
        : isUrgent
            ? "hsl(0 60% 18% / 0.4)"
            : "hsl(350 50% 16% / 0.3)";
    const rowBorder = isDone
        ? "hsl(45 85% 55% / 0.35)"
        : isUrgent
            ? "hsl(0 80% 55% / 0.5)"
            : "hsl(350 70% 50% / 0.35)";

    const Wrapper = item.action ? "button" : "div";
    const wrapperProps = item.action
        ? { onClick: item.action, type: "button" }
        : {};

    return (
        <li>
            <Wrapper
                {...wrapperProps}
                data-testid={`due-out-${item.id}`}
                className={`w-full text-left p-3 rounded-lg border transition-colors flex items-center gap-3 ${item.action ? "hover:brightness-110 cursor-pointer" : ""}`}
                style={{ background: rowBg, borderColor: rowBorder }}
            >
                {/* Status dot */}
                <span className="relative inline-flex items-center justify-center w-6 h-6 shrink-0">
                    {dotPulse && (
                        <span
                            aria-hidden="true"
                            className="absolute inset-0 rounded-full animate-ping"
                            style={{ background: dotColor, opacity: 0.5 }}
                        />
                    )}
                    <span
                        className="relative w-3 h-3 rounded-full"
                        style={{ background: dotColor, boxShadow: `0 0 8px ${dotColor}` }}
                    />
                </span>

                {/* Icon + content */}
                <Icon
                    size={14}
                    className={isDone ? "text-[hsl(45_85%_68%)]" : isUrgent ? "text-[hsl(0_80%_70%)]" : "text-[hsl(350_75%_75%)]"}
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium truncate ${isDone ? "text-[hsl(45_85%_85%)]" : "text-foreground"}`}>
                            {item.label}
                        </span>
                        {item.count != null && item.count > 0 && (
                            <span
                                className="text-[9px] font-mono px-1.5 py-0.5 rounded-full"
                                style={{ background: dotColor, color: "hsl(151 55% 8%)" }}
                                data-testid={`due-out-${item.id}-count`}
                            >
                                {item.count}
                            </span>
                        )}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                        {item.sub}
                    </div>
                </div>

                {/* Status icon */}
                {isDone ? (
                    <CheckCircle2 size={14} className="text-[hsl(45_85%_68%)] shrink-0" />
                ) : isUnknown ? (
                    <Clock size={14} className="text-muted-foreground shrink-0" />
                ) : (
                    <>
                        {isUrgent && <AlertTriangle size={12} className="text-[hsl(0_80%_70%)] shrink-0" />}
                        {item.action && <ChevronRight size={14} className="text-muted-foreground shrink-0" />}
                    </>
                )}
            </Wrapper>
        </li>
    );
}
